import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * <ReferralProvider> — Fase 7
 * Captures `?ref=<code>` from any URL and persists it in localStorage. Attaches
 * the code to the current cart session (server-side). Exposes hooks for
 * consumers (landing pages, cart) to preview which code is active.
 */
const STORAGE_KEY = "dcc_referral_code";
const SESSION_KEY = "dcc_cart_session";

const Ctx = createContext({
  referralCode: null,
  ownerName: null,
  ownerType: null,
  commissionPct: null,
  attach: async () => false,
  clear: () => {},
});

export function ReferralProvider({ children }) {
  const [referralCode, setReferralCode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [meta, setMeta] = useState({ ownerName: null, ownerType: null, commissionPct: null });

  const attach = useCallback(async (code) => {
    if (!code) return false;
    const upper = code.toUpperCase();
    try {
      // Resolve first — validates & tracks the click event server-side
      const resolveRes = await api.get(`/public/referral/${upper}`);
      if (!resolveRes.data?.valid) {
        return false;
      }
      // Attach to the current session cart
      let sessionId = null;
      try {
        sessionId = localStorage.getItem(SESSION_KEY);
        if (!sessionId) {
          sessionId = "dcc-" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
          localStorage.setItem(SESSION_KEY, sessionId);
        }
      } catch { /* private mode */ }
      try {
        await api.post(`/public/referral/${upper}/attach-to-cart`,
          { session_id: sessionId },
          { headers: sessionId ? { "X-Session-Id": sessionId } : {} });
      } catch { /* even if attach fails, remember locally */ }
      try {
        localStorage.setItem(STORAGE_KEY, upper);
      } catch { /* ignore */ }
      setReferralCode(upper);
      setMeta({
        ownerName: resolveRes.data.owner_name,
        ownerType: resolveRes.data.owner_type,
        commissionPct: resolveRes.data.commission_pct,
      });
      track("referral_cart_attached", { code: upper, owner_type: resolveRes.data.owner_type });
      return true;
    } catch (_e) {
      return false;
    }
  }, []);

  const clear = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setReferralCode(null);
    setMeta({ ownerName: null, ownerType: null, commissionPct: null });
  }, []);

  // Auto-detect ?ref= param on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const codeFromUrl = params.get("ref");
      if (codeFromUrl) {
        track("referral_click", { code: codeFromUrl.toUpperCase(), source: "url_param" });
        attach(codeFromUrl);
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Ctx.Provider value={{ referralCode, ...meta, attach, clear }}>
      {children}
    </Ctx.Provider>
  );
}

export function useReferral() {
  return useContext(Ctx);
}
